import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ThemeState } from '../models/ConfigurationThemeConfig';
import { THEME_STORAGE_KEY } from '../../components/ThemeConfigProvider/ThemeConfigProvider';

export const loadThemeFromStorage = (): ThemeState | null => {
  const storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
  return storedTheme ? JSON.parse(storedTheme) : null;
};

/**
 * Определяет системную тему пользователя
 */
export const getSystemTheme = (): 'light' | 'dark' => {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
};

/**
 * Получает эффективную тему с учетом системной
 */
export const getEffectiveTheme = (mode: 'light' | 'dark' | 'system'): 'light' | 'dark' => {
  if (mode === 'system') {
    return getSystemTheme();
  }
  return mode;
};

const initialState: ThemeState = loadThemeFromStorage() || {
  mode: 'system',
  configName: null,
  config: null,
  baseMode: getSystemTheme(),
};

const updateThemeStorage = (newState: Partial<ThemeState>) => {
  try {
    const currentState = loadThemeFromStorage() || initialState;
    const updatedState = { ...currentState, ...newState };
    localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(updatedState));
  } catch (error) {
    console.error('Failed to update theme in localStorage:', error);
  }
};

const themeSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    setTheme(state, action: PayloadAction<Partial<ThemeState>>) {
      // Обновляем состояние на основе переданных данных
      Object.assign(state, action.payload);

      // Если выбрана НЕ кастомная тема, очищаем связанные с ней поля
      if (action.payload.mode && action.payload.mode !== 'custom') {
        state.configName = null;
        state.config = null;
      }

      // Определяем и сохраняем актуальный базовый режим
      if (state.mode === 'system') {
        state.baseMode = getSystemTheme();
      } else if (state.mode !== 'custom') {
        state.baseMode = state.mode;
      }

      // Сохраняем все состояние в localStorage
      localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify(state));
    },
  },
});

export const { setTheme } = themeSlice.actions;

const ThemeReducer = themeSlice.reducer;

export default ThemeReducer;